/**
 * Números da Home (Versão 3B do protótipo design/prototipos/dashboard-mockup), calculados a
 * partir das listas do usuário (GET /app/list) e da sessão. Funções puras: o hook
 * useHomeDashboard só junta as fontes.
 */
import { distanceKm, type FindMunicipality } from './municipalities';

export type HomeModule = 'places' | 'phones' | 'emails';

export const HOME_MODULES: HomeModule[] = ['places', 'phones', 'emails'];

export interface HomeList {
  id: string;
  module: HomeModule;
  count: number;
  /** Só nas listas de negócios locais: "lat,lng|nome curto". */
  locations: string[];
}

export interface CitySummary {
  key: string;
  name: string;
  /** Sigla do estado; null fora do Brasil. */
  uf: string | null;
  lat: number;
  lng: number;
  capital: boolean;
  businesses: number;
  listIds: string[];
}

export interface RegionSummary {
  key: string;
  /** Nome da capital do grupo ou da cidade com mais negócios. */
  name: string;
  /** Mais de uma cidade: a etiqueta vira "Curitiba e região". */
  grouped: boolean;
  lat: number;
  lng: number;
  businesses: number;
  lists: number;
  cities: CitySummary[];
}

export interface Coverage {
  cities: number;
  /** Estados, do que tem mais negócios para o que tem menos. */
  ufs: string[];
  /** Há locais fora do Brasil (ou que a tabela de municípios não reconheceu). */
  abroad: boolean;
}

export type LicenseState = 'active' | 'ending' | 'expired' | 'subscription' | 'lifetime';

export interface LicenseInfo {
  state: LicenseState;
  until: Date | null;
  daysLeft: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;
/** Cidades a até esta distância formam uma região ("Florianópolis e região"). */
export const REGION_RADIUS_KM = 120;
/** Locais fora do Brasil a até esta distância contam como a mesma cidade. */
const ABROAD_SAME_CITY_KM = 15;

export interface ParsedLocation {
  lat: number;
  lng: number;
  name: string;
}

/** "-25.42,-49.27|Curitiba" → { lat, lng, name }. */
export function parseLocation(tag: string): ParsedLocation | null {
  const [geo = '', name = ''] = tag.split('|');
  const [latRaw, lngRaw] = geo.split(',');
  const lat = Number(latRaw);
  const lng = Number(lngRaw);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng, name: name.trim() };
}

/**
 * Negócios e listas por cidade. Uma lista com vários locais divide os negócios entre eles
 * (a lista não diz quantos vieram de cada local).
 */
export function summarizeCities(lists: HomeList[], find: FindMunicipality | null): CitySummary[] {
  const byKey = new Map<string, CitySummary>();
  const abroad: CitySummary[] = [];
  for (const list of lists) {
    if (list.module !== 'places' || list.count <= 0) continue;
    const places = list.locations.map(parseLocation).filter((p): p is ParsedLocation => p !== null);
    if (places.length === 0) continue;
    const share = list.count / places.length;
    for (const place of places) {
      const m = find ? find(place.lat, place.lng, place.name) : null;
      let city: CitySummary | undefined;
      if (m) {
        const key = `br:${m.code}`;
        city = byKey.get(key);
        if (!city) {
          city = { key, name: m.name, uf: m.uf, lat: m.lat, lng: m.lng, capital: m.capital, businesses: 0, listIds: [] };
          byKey.set(key, city);
        }
      } else {
        city = abroad.find((c) => distanceKm(c, place) <= ABROAD_SAME_CITY_KM);
        if (!city) {
          city = {
            key: `ext:${place.lat.toFixed(2)}:${place.lng.toFixed(2)}`,
            name: place.name || '—',
            uf: null,
            lat: place.lat,
            lng: place.lng,
            capital: false,
            businesses: 0,
            listIds: [],
          };
          abroad.push(city);
          byKey.set(city.key, city);
        }
      }
      city.businesses += share;
      if (!city.listIds.includes(list.id)) city.listIds.push(list.id);
    }
  }
  return [...byKey.values()]
    .map((c) => ({ ...c, businesses: Math.round(c.businesses) }))
    .sort((a, b) => b.businesses - a.businesses);
}

/** Junta as cidades próximas em volta da que tem mais negócios. */
export function groupRegions(cities: CitySummary[], radiusKm = REGION_RADIUS_KM): RegionSummary[] {
  const used = new Set<string>();
  const regions: RegionSummary[] = [];
  for (const main of cities) {
    if (used.has(main.key)) continue;
    const members = cities.filter((c) => !used.has(c.key) && distanceKm(main, c) <= radiusKm);
    members.forEach((c) => used.add(c.key));
    // O nome e o ponto da região vêm da capital do grupo, se houver; senão, da maior cidade.
    const anchor = members.find((c) => c.capital) ?? main;
    regions.push({
      key: anchor.key,
      name: anchor.name,
      grouped: members.length > 1,
      lat: anchor.lat,
      lng: anchor.lng,
      businesses: members.reduce((sum, c) => sum + c.businesses, 0),
      lists: new Set(members.flatMap((c) => c.listIds)).size,
      cities: members,
    });
  }
  return regions.sort((a, b) => b.businesses - a.businesses);
}

export function coverageOf(cities: CitySummary[]): Coverage {
  const byUf = new Map<string, number>();
  for (const c of cities) if (c.uf) byUf.set(c.uf, (byUf.get(c.uf) ?? 0) + c.businesses);
  return {
    cities: cities.length,
    ufs: [...byUf.entries()].sort((a, b) => b[1] - a[1]).map(([uf]) => uf),
    abroad: cities.some((c) => c.uf === null),
  };
}

/** Estado da licença para o selo, com as mesmas faixas da barra do rodapé. */
export function licenseFrom(expiredAt: string | undefined, now: Date): LicenseInfo | null {
  if (!expiredAt) return null;
  const until = new Date(expiredAt);
  if (Number.isNaN(until.getTime())) return null;
  const left = until.getTime() - now.getTime();
  const daysLeft = Math.ceil(left / DAY_MS);
  if (left > 365 * DAY_MS * 10) return { state: 'lifetime', until: null, daysLeft };
  if (left >= 366 * DAY_MS) return { state: 'subscription', until: null, daysLeft };
  if (left <= 0) return { state: 'expired', until, daysLeft };
  return { state: daysLeft < 4 ? 'ending' : 'active', until, daysLeft };
}
